export const TAG_TABLE_COLUMNS = [
    {
        key: 'name',
        label: 'tags.tagList.columnTitle.name',
        draggable: true,
        resizeable: true,
        sortable: true,
    },
    {
        key: 'assignedAssets',
        label: 'tags.tagList.columnTitle.assignedAssets',
        draggable: true,
        resizeable: true,
        sortable: true,
    },
    {
        key: 'assignedDrivers',
        label: 'tags.tagList.columnTitle.assignedDrivers',
        draggable: true,
        resizeable: true,
        sortable: true,
    },
] as const;

type TagTableColumnKey = (typeof TAG_TABLE_COLUMNS)[number]['key'];

export type TagTableColumnConfig = (typeof TAG_TABLE_COLUMNS)[number] & {
    visible: boolean;
    width?: number | string;
};

export type TagListSortKey = TagTableColumnKey;

export const TAG_TABLE_COLUMNS_STORAGE_KEY = 'tag-administration.table';

export const defaultTagTableColumns = (): TagTableColumnConfig[] =>
    TAG_TABLE_COLUMNS.map(column => ({
        ...column,
        visible: true,
    }));

export const getTagTableColumnsStorageKey = (accountId: string | null | undefined) => {
    const accountPrefix = accountId?.slice(0, 6) ?? 'default';

    return `${TAG_TABLE_COLUMNS_STORAGE_KEY}:${accountPrefix}`;
};

export const normalizeTagTableColumns = (storedColumns: TagTableColumnConfig[] | undefined): TagTableColumnConfig[] => {
    const defaultColumns = defaultTagTableColumns();

    if (!storedColumns?.length) {
        return defaultColumns;
    }

    const defaultColumnsByKey = new Map(defaultColumns.map(column => [column.key, column]));
    const normalizedColumns = storedColumns.flatMap(storedColumn => {
        const defaultColumn = defaultColumnsByKey.get(storedColumn.key);

        if (!defaultColumn) {
            return [];
        }

        return [
            {
                ...defaultColumn,
                visible: storedColumn.visible,
                width: storedColumn.width ?? defaultColumn.width,
            },
        ];
    });

    const columnsWithMissingDefaults: TagTableColumnConfig[] = [...normalizedColumns];

    for (const defaultColumn of defaultColumns) {
        if (columnsWithMissingDefaults.some(column => column.key === defaultColumn.key)) {
            continue;
        }

        const precedingDefaultKeys = defaultColumns
            .slice(
                0,
                defaultColumns.findIndex(column => column.key === defaultColumn.key)
            )
            .map(column => column.key);
        const insertionIndex = [...columnsWithMissingDefaults]
            .reverse()
            .findIndex(column => precedingDefaultKeys.includes(column.key));

        if (insertionIndex === -1) {
            columnsWithMissingDefaults.unshift(defaultColumn);
            continue;
        }

        const targetIndex = columnsWithMissingDefaults.length - insertionIndex;
        columnsWithMissingDefaults.splice(targetIndex, 0, defaultColumn);
    }

    return columnsWithMissingDefaults;
};
